import {
  CARE_FREQUENCY,
  CURRENT_CARE_BASE_TYPES,
  CURRENT_CARE_FACILITY_TYPES,
  HEALTH_INSURANCE_TYPES,
  REFERRAL_SOURCE,
  REQUIRED_CARE_TYPES,
  SEARCH_TYPE_CARE,
  SOLUTION_SHORT_TERM_STATIC_CARE,
  SOLUTION_STATIC_CARE,
  SPECIAL_CARE_NEEDS,
  VISIT_TIME,
} from "core/consts";
import { isProd } from "core/model/utils/featureFlags";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { grammaticalList } from "core/model/utils/strings";
import {
  Auction,
  CareFrequency,
  CurrentCareBaseType,
  CurrentCareFacilityType,
  GetOntologyType,
  HealthInsuranceType,
  ReferralSource,
  SearchType,
  Solutions,
  SpecialCareNeeds,
  VisitTime,
} from "core/types";
import Translations from "translations/types";
import { CategoryType } from "./shared";

export type Filter = {
  exists: (value: Auction, forClinic: boolean) => boolean;
  notEmpty?: (value: Auction) => boolean;
  valueGetter: (value: Auction) => any;
};

export type ComponentMappingItem = {
  Component: React.ComponentType<any>;
  fullWidth?: boolean;
  key: string;
};

export function getCategories({
  auction,
  componentMapping,
  filters,
  forClinic,
  getWhitelist,
}: {
  auction: Auction;
  componentMapping: ComponentMappingItem[];
  filters: Record<string, Filter>;
  forClinic: boolean;
  getWhitelist?: (params: {
    formInputValue: Auction;
  }) => Record<string, boolean>;
}): CategoryType[] {
  let filteredMapping = componentMapping;

  if (getWhitelist) {
    const whitelist = getWhitelist({ formInputValue: auction });

    filteredMapping = componentMapping.filter(({ key }) => {
      const display = whitelist[key];
      if (display == null && !isProd) {
        console.error("Missing whitelist for component", key);
        return true;
      }
      return display === true;
    });
  }

  return filteredMapping.map(({ Component, fullWidth, key }) => {
    const { exists, notEmpty, valueGetter } = filters[key];

    return {
      Component,
      exists: exists(auction, forClinic),
      fullWidth: !!fullWidth,
      key,
      notEmpty: notEmpty?.(auction),
      valueGetter,
    };
  });
}

export function isNumberAndNotZero(value: number | undefined): boolean {
  return value != null && value !== 0;
}

export const isNotEmptyString = (value: string | null | undefined): boolean =>
  value != null && value.length > 0;

export const stringFilled = (value: string | null | undefined) => value != null;

export const descriptionCategory = (
  value: { description?: string } | null | undefined,
) => value != null && isNotEmptyString(value.description);

export function isNullValue(
  value: boolean | number | string | null | undefined,
  noEmpty?: boolean,
) {
  switch (typeof value) {
    case "string":
      return value === null || (noEmpty && value === "");
    case "boolean":
      return value === null;
    case "number":
      return !value;
    default:
      return true;
  }
}

export const getKey = (value: string, prefix: string | null | undefined) =>
  `${prefix || ""}-${value}`;

export function computeChipProps(props: {
  isBold?: boolean;
  isDisabled?: boolean;
  isPrimary?: boolean;
  isSelected?: boolean;
}) {
  if (props.isDisabled) {
    if (props.isSelected) return { isBold: true, isDisabled: true };
    return { isBold: true };
  }
  return props;
}

export function getColonedPrefix(prefix: string, translations: Translations) {
  return `${prefix}${translations.general.colon}`;
}

export function generatePrefixedString(
  translations: Translations,
  str: string,
  prefix: string | null | undefined,
  suffix?: string | null,
) {
  const strValue =
    suffix && isNotEmptyString(suffix) ? `${str} ${suffix}` : str;
  return prefix && isNotEmptyString(prefix)
    ? `${getColonedPrefix(prefix, translations)} ${strValue}`
    : strValue;
}

// Accepts object and array of keys to check
export const inputCollectionIsNotEmpty = ({
  assertionObject,
  keysToExist,
}: {
  assertionObject?: Record<string, any>;
  keysToExist: Array<string>;
}): boolean => {
  if (!assertionObject || !keysToExist) return false;
  return keysToExist.some((key) => {
    const chunk = assertionObject[key];
    return (
      chunk !== undefined && chunk !== null && chunk !== false && chunk !== 0
    );
  });
};

export const activableInputCollectionIsNotEmpty = (
  collection: Record<string, any>,
): boolean => {
  if (!collection) {
    return false;
  }

  // Get all keys from the object EXCEPT for '__typename'
  const keys = Object.keys(collection).filter((key) => key !== "__typename");

  // Check if any of the values for those remaining keys are not null
  return keys.some((key) => collection[key] !== null);
};

export const exists = (categories: Array<any>) => {
  for (let i = 0; i < categories.length; i++) {
    if (categories[i].exists) return true;
  }

  return false;
};

export function shouldHideStreetAndHouseNumber(
  search_type: SearchType | undefined,
  solutions: Solutions | undefined,
): boolean {
  return (
    search_type === SEARCH_TYPE_CARE &&
    Array.isArray(solutions) &&
    solutions.length > 0 &&
    solutions.every(
      (s) =>
        s === SOLUTION_STATIC_CARE || s === SOLUTION_SHORT_TERM_STATIC_CARE,
    )
  );
}

export function getReferralSourceLabel(
  getOntology: GetOntologyType,
  translations: Translations,
  referralSources?: ReferralSource[],
  referralSourceOther?: string | null,
  isStatic = false,
): string {
  if (!referralSources) {
    return translations.careproviderApp.recareAdmissionsProfile.unknown;
  }

  const combinedLabels = referralSources
    ?.clone()
    ?.sort((a, b) => a - b)
    ?.map((key) => {
      const label = getOntology({
        key,
        type: isStatic
          ? ONTOLOGIES.REFERRAL_SOURCE_STATIC
          : ONTOLOGIES.REFERRAL_SOURCE_MOBILE,
      });

      if (key === REFERRAL_SOURCE.OTHER && referralSourceOther) {
        return referralSourceOther;
      }

      return label;
    });

  return combinedLabels
    ? grammaticalList({
        array: combinedLabels,
        translations,
      })
    : translations.careproviderApp.recareAdmissionsProfile.unknown;
}

export function getInsuranceLabel(
  getOntology: GetOntologyType,
  translations: Translations,
  insurance?: HealthInsuranceType,
  insuranceOther?: string | null,
): string {
  if (insuranceOther) {
    return insuranceOther;
  }

  if (!insurance) {
    return translations.careproviderApp.recareAdmissionsProfile.unknown;
  }

  if (!Object.values(HEALTH_INSURANCE_TYPES).includes(insurance)) {
    return translations.general.other;
  }

  return getOntology({
    key: insurance,
    type: ONTOLOGIES.INSURANCE_TYPE,
  });
}

export function getRequiredCareTypeLabel({
  auction,
  getOntology,
  translations,
}: {
  getOntology: GetOntologyType;
  auction: Auction;
  translations: Translations;
}): string {
  const combinedRequiredCareTypeLabels =
    auction?.admissions?.profile?.required_care_types
      ?.clone()
      ?.sort((a, b) => a - b)
      ?.map((key) => {
        const label = getOntology({
          key,
          type: ONTOLOGIES.REQUIRED_CARE_TYPES,
        });
        switch (key) {
          case REQUIRED_CARE_TYPES.BASIC: {
            const basicCareTypes =
              auction?.admissions?.profile?.required_care_basic_types ?? [];

            const requiredCareBasicTypeLabels =
              basicCareTypes
                ?.clone()
                ?.sort((a, b) => a - b)
                ?.map((basicTypeKey) =>
                  getOntology({
                    key: basicTypeKey,
                    type: ONTOLOGIES.REQUIRED_CARE_BASIC_TYPES,
                  }),
                ) ?? [];
            return [label, ...requiredCareBasicTypeLabels];
          }
          case REQUIRED_CARE_TYPES.OTHER: {
            const careOther =
              auction?.admissions?.profile?.required_care_other?.decrypted;
            if (careOther) {
              return careOther;
            }

            return label;
          }

          default:
            return label;
        }
      })
      .flatMap((label) => label);

  return grammaticalList({
    array: combinedRequiredCareTypeLabels,
    translations,
  });
}

export function getFrequencyLabel(
  getOntology: GetOntologyType,
  translations: Translations,
  frequency?: CareFrequency,
  frequencyOther?: string | null,
): string {
  if (!frequency) {
    return translations.careproviderApp.recareAdmissionsProfile.unknown;
  }

  if (frequency === CARE_FREQUENCY.DONT_KNOW && frequencyOther) {
    return frequencyOther;
  }

  return getOntology({ key: frequency, type: ONTOLOGIES.CARE_FREQUENCY });
}

export function getVisitTimeLabel(
  getOntology: GetOntologyType,
  translations: Translations,
  visitTimes?: VisitTime[],
  visitTimeOther?: string | null,
): string {
  if (!visitTimes) {
    return translations.careproviderApp.recareAdmissionsProfile.unknown;
  }

  const combinedLabels = visitTimes
    ?.clone()
    ?.sort((a, b) => a - b)
    ?.map((key) => {
      const label = getOntology({ key, type: ONTOLOGIES.VISIT_TIME });
      if (key === VISIT_TIME.OTHER && visitTimeOther) {
        return visitTimeOther;
      }
      return label;
    })
    .flatMap((label) => label);

  return combinedLabels
    ? grammaticalList({
        array: combinedLabels,
        translations,
      })
    : translations.careproviderApp.recareAdmissionsProfile.unknown;
}

export function getCurrentCareLabel(
  getOntology: GetOntologyType,
  translations: Translations,
  currentCareTypes?: CurrentCareBaseType[],
  currentCareFacilityTypes?: CurrentCareFacilityType[],
  currentCareFacilityOther?: string | null,
): string {
  if (!currentCareTypes) {
    return translations.careproviderApp.recareAdmissionsProfile.unknown;
  }

  const combinedRequiredCareTypeLabels = currentCareTypes
    ?.clone()
    ?.sort((a, b) => a - b)
    ?.map((key) => {
      const label = getOntology({ key, type: ONTOLOGIES.CURRENT_CARE });
      if (key === CURRENT_CARE_BASE_TYPES.FACILITY) {
        const facilityLabels =
          currentCareFacilityTypes
            ?.clone()
            ?.sort((a, b) => a - b)
            ?.map((facilityKey) => {
              const facilityLabel = getOntology({
                key: facilityKey,
                type: ONTOLOGIES.CURRENT_CARE_FACILITY_TYPES,
              });

              if (
                facilityKey === CURRENT_CARE_FACILITY_TYPES.OTHER &&
                currentCareFacilityOther
              ) {
                return currentCareFacilityOther;
              }

              return facilityLabel;
            }) ?? [];

        return [label, ...facilityLabels];
      }

      return label;
    })
    .flatMap((label) => label);

  return combinedRequiredCareTypeLabels
    ? grammaticalList({
        array: combinedRequiredCareTypeLabels,
        translations,
      })
    : translations.careproviderApp.recareAdmissionsProfile.unknown;
}

export function getSpecialCareNeedsLabel(
  getOntology: GetOntologyType,
  translations: Translations,
  specialCareNeeds?: SpecialCareNeeds[],
  specialCareNeedsOther?: string | null,
): string {
  if (!specialCareNeeds) {
    return translations.careproviderApp.recareAdmissionsProfile.unknown;
  }

  const combinedLabels = specialCareNeeds
    ?.clone()
    ?.sort((a, b) => a - b)
    ?.map((key) => {
      const label = getOntology({ key, type: ONTOLOGIES.SPECIAL_CARE_NEEDS });
      if (key === SPECIAL_CARE_NEEDS.OTHER && specialCareNeedsOther) {
        return specialCareNeedsOther;
      }

      return label;
    });

  return combinedLabels
    ? grammaticalList({
        array: combinedLabels,
        translations,
      })
    : translations.careproviderApp.recareAdmissionsProfile.unknown;
}
